import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Box, TextField, Button, Grid, Typography,
    Card, CardContent, CardMedia, CardActionArea,
    Pagination, IconButton, InputAdornment, Chip,
    Select, MenuItem, FormControl, InputLabel, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../AuthProvider';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from '../authUtils';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '1200px',
    height: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 0,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

const ProductSelectorModal = ({
    open,
    onClose,
    onSelect,
    baseUrl
}) => {
    // Auth hooks
    const { account } = useAuth();
    const { instance } = useMsal();

    const [query, setQuery] = useState('');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filters State
    const [filters, setFilters] = useState({
        brand: '',
        category: '',
        subcategory: '',
        minPrice: '',
        maxPrice: ''
    });

    // Dynamic Options State (Fetched from API)
    const [filterOptions, setFilterOptions] = useState({
        brands: [],
        categories: [],
        subcategories: []
    });

    const [showFilters, setShowFilters] = useState(false);

    // --- 1. FETCH FILTERS FROM API (Source of Truth) ---
    const fetchFilters = useCallback(async () => {
        if (!account || !open) return;
        try {
            const token = await getApiToken(instance, account);
            const params = new URLSearchParams();
            if (filters.category) params.append('category', filters.category);
            if (filters.brand) params.append('brand', filters.brand);
            if (filters.subcategory) params.append('subcategory', filters.subcategory);

            // Calls the Smart Search Filter endpoint (which now pulls from Ecom)
            const url = `${baseUrl}/api/v2/smart-search/filters?${params.toString()}`;

            const response = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
            if (response.ok) {
                const data = await response.json();
                setFilterOptions(data);
            }
        } catch (e) { console.error("Filter fetch error", e); }
    }, [account, instance, open, baseUrl, filters.category, filters.brand, filters.subcategory]);

    // Fetch filters when modal opens or filter selection changes (cascading)
    useEffect(() => { fetchFilters(); }, [fetchFilters]);

    // --- 2. SEARCH PRODUCTS ---
    const searchProducts = useCallback(async (pageNo = 1) => {
        if (!account || !open) return;
        setLoading(true);
        try {
            const token = await getApiToken(instance, account);
            const params = new URLSearchParams({
                page: pageNo - 1,
                size: 12,
                query: query,
                ...(filters.brand && { brand: filters.brand }),
                ...(filters.category && { category: filters.category }),
                ...(filters.subcategory && { subcategory: filters.subcategory }),
                // Pass Price Filters to Backend
                ...(filters.minPrice && { minPrice: filters.minPrice }),
                ...(filters.maxPrice && { maxPrice: filters.maxPrice })
            });

            // Use Catalog-Only endpoint
            const url = `${baseUrl}/api/v2/smart-search/catalog-only?${params.toString()}`;

            const response = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
            const data = await response.json();

            if (data) {
                setProducts(data.content || []);
                setTotalPages(data.totalPages || 1);
                setPage(pageNo);
            }
        } catch (error) {
            console.error("Catalog search failed:", error);
        } finally {
            setLoading(false);
        }
    }, [account, instance, open, baseUrl, query, filters]);

    // Initial load
    useEffect(() => {
        if (open) {
            searchProducts(1);
        }
    }, [open, searchProducts]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') searchProducts(1);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            ...(field === 'category' ? { subcategory: '' } : {}) // Reset subcat if category changes
        }));
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                {/* --- HEADER --- */}
                <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8f9fa' }}>
                    <Typography variant="h6" component="h2">Select Product from Catalog</Typography>
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </Box>

                {/* --- SEARCH & FILTERS BAR --- */}
                <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={showFilters ? 4 : 8}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by name, brand, or description..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyPress}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant={showFilters ? "contained" : "outlined"}
                                onClick={() => setShowFilters(!showFilters)}
                                startIcon={<FilterListIcon />}
                            >
                                Filters
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button fullWidth variant="contained" onClick={() => searchProducts(1)}>Search</Button>
                        </Grid>
                    </Grid>

                    {/* Collapsible Filters */}
                    {showFilters && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {/* Category - 3 cols */}
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={filters.category}
                                        label="Category"
                                        onChange={(e) => handleFilterChange('category', e.target.value)}
                                    >
                                        <MenuItem value=""><em>All Categories</em></MenuItem>
                                        {filterOptions.categories?.map((c) => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Subcategory - 3 cols */}
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Subcategory</InputLabel>
                                    <Select
                                        value={filters.subcategory}
                                        label="Subcategory"
                                        disabled={!filters.category}
                                        onChange={(e) => handleFilterChange('subcategory', e.target.value)}
                                    >
                                        <MenuItem value=""><em>All Subcategories</em></MenuItem>
                                        {filterOptions.subcategories?.map((s) => (
                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Brand - 3 cols */}
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Brand</InputLabel>
                                    <Select
                                        value={filters.brand}
                                        label="Brand"
                                        onChange={(e) => handleFilterChange('brand', e.target.value)}
                                    >
                                        <MenuItem value=""><em>All Brands</em></MenuItem>
                                        {filterOptions.brands?.map((b) => (
                                            <MenuItem key={b} value={b}>{b}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Price Range - 3 cols (1.5 + 1.5) */}
                            <Grid item xs={6} md={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Min Price"
                                    type="number"
                                    value={filters.minPrice}
                                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                />
                            </Grid>
                            <Grid item xs={6} md={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Max Price"
                                    type="number"
                                    value={filters.maxPrice}
                                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Box>

                {/* --- RESULTS GRID --- */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f4f6f8' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>
                    ) : products.length === 0 ? (
                        <Box sx={{ textAlign: 'center', mt: 5, color: '#666' }}>
                            <Typography variant="h6">No products found.</Typography>
                            <Typography variant="body2">Try adjusting your search or filters.</Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {products.map((product) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={product.ecomProductId || product.id}>
                                    <Card
                                        sx={{
                                            height: '100%', display: 'flex', flexDirection: 'column',
                                            transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                                        }}
                                    >
                                        <CardActionArea onClick={() => onSelect(product)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                            <Box sx={{ pt: '100%', position: 'relative', bgcolor: '#fff' }}>
                                                <CardMedia
                                                    component="img"
                                                    image={product.ecomThumbnailUrl || product.thumbnailUrl || "https://via.placeholder.com/150"}
                                                    alt={product.productName || product.name}
                                                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', p: 2 }}
                                                />
                                            </Box>
                                            <CardContent sx={{ flexGrow: 1, borderTop: '1px solid #eee' }}>
                                                {/* Brand/Vendor */}
                                                <Typography variant="caption" display="block" sx={{ color: '#e67e22', fontWeight: 'bold', mb: 0.5 }}>
                                                    {product.brandName || product.lastVendor || 'Unknown Brand'}
                                                </Typography>

                                                {/* Product Name */}
                                                <Typography gutterBottom variant="subtitle2" component="div" sx={{ lineHeight: 1.3, height: '2.6em', overflow: 'hidden' }}>
                                                    {product.productName || product.name}
                                                </Typography>

                                                {/* Category & Price */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                                    <Chip label={product.omsCategory || product.categoryName || 'General'} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                                                    <Typography variant="body2" color="primary" fontWeight="bold">
                                                        ₹{product.ecomMrp || product.price || 0}
                                                    </Typography>
                                                </Box>

                                                {(product.colors || product.sizes) && (
                                                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {product.colors && product.colors.split(',').slice(0, 3).map((c) => (
                                                            <Chip key={c} label={c.trim()} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                                                        ))}
                                                        {product.sizes && (
                                                            <Chip label={`Sizes: ${product.sizes.split(',').length}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                                                        )}
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>

                {/* --- FOOTER (PAGINATION) --- */}
                <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'center', bgcolor: '#fff' }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, v) => searchProducts(v)}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </Box>
        </Modal>
    );
};

export default ProductSelectorModal;